import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import accountApi from '~/apis/account';
import { CloseIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import { ROLE_TECHINICIAN } from '~/utils/constants/account';
import { handleApiError, notify } from '~/utils/helper';
import { IAccount, IUpdatePassword } from '~/utils/interface/account';
import technicianValidation from '~/validation/technician';

type UpdatePasswordDialogProps = {
  open: boolean;
  data?: IAccount;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  setOpenBasicInfoUserDialog?: (open: boolean, needRefreshData?: boolean) => void;
};

const initialValues: IUpdatePassword = {
  username: '',
  new_password: '',
};

const UpdatePasswordDialog = (props: UpdatePasswordDialogProps) => {
  const { t } = useTranslation('admin/technician');

  const form = useFormik({
    validateOnChange: false,
    validationSchema: technicianValidation(t).updatePassword,
    initialValues,
    onSubmit: async (values) => {
      try {
        if (props.data?.username) {
          await accountApi.updatePassword(
            { role: ROLE_TECHINICIAN },
            { ...values, username: String(props.data?.username) }
          );
          handleSetOpen(false, false);
          notify('success', t('editTechnicianDialog.editSuccess'));
        }
      } catch (e: any) {
        handleApiError(e, true);
      }
    },
  });

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  return (
    <Box className='w-full'>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle className='bg-[#ED1002]'>
          {t('updatePassword.title')}
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box className='font-bold text-[#005F73] text-[18px] mb-5'>{t('detailTechnicianDialog.title')}</Box>
          <BaseRow>
            <Box className='w-full relative'>
              <Box className='flex flex-row gap-[15px] mb-[16px] justify-start'>
                <Box className='font-bold'>{t('detailTechnicianDialog.name2')}:</Box>
                <Box className='font-semibold'>{props.data?.name}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[16px] justify-start'>
                <Box className='font-bold'>{t('detailTechnicianDialog.phone')}:</Box>
                <Box className='font-semibold'>{props.data?.phone}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[16px] justify-start'>
                <Box className='font-bold'>{t('detailTechnicianDialog.email')}:</Box>
                <Box className='font-semibold'>{props.data?.email}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[20px] justify-start'>
                <Box className='font-bold'>{t('detailTechnicianDialog.address')}:</Box>
                <Box className='font-semibold'>{props.data?.address}</Box>
              </Box>
            </Box>
          </BaseRow>
          <Box className='font-bold text-[#005F73] text-[18px] mb-2.5'>{t('updatePassword.accountInfo')}</Box>
          <Box component='form'>
            <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
              <Box className='font-bold'>{t('updatePassword.name')}</Box>
              <Typography fontWeight={'600'}>{props.data?.username}</Typography>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-1.5 !items-center'>
              <Box className='w-1/2 font-bold flex items-center'>
                <InputLabel required={true}>{t('updatePassword.newPassword')}:</InputLabel>
              </Box>
              <TextField
                size='medium'
                className='w-1/2'
                type='password'
                name='new_password'
                placeholder={t('updatePassword.newPassword')}
                value={form.values?.new_password}
                onChange={form.handleChange}
                error={Boolean(form.errors?.new_password)}
                helperText={form.errors?.new_password}
              />
            </BaseRow>
            <BaseRow className='gap-[20px] mb-1.5 !items-center'>
              <Box className='w-1/2 font-bold flex items-center'>
                <InputLabel>{t('updatePassword.repeatNewPassword')}:</InputLabel>
              </Box>
              <TextField
                size='medium'
                className='w-1/2'
                type='password'
                name='new_password_confirmation'
                placeholder={t('updatePassword.repeatNewPassword')}
                onChange={form.handleChange}
                error={Boolean(form.errors?.new_password_confirmation)}
                helperText={form.errors?.new_password_confirmation}
              />
            </BaseRow>
            <Box className='mt-5 text-right'>
              <Button
                variant='outlined'
                className='min-w-[86px] bg-white text-[#ED1002] border border-[#ED1002] mr-[20px]'
                onClick={() => handleSetOpen(false)}
              >
                {t('cancel')}
              </Button>
              <Button
                variant='contained'
                color='secondary'
                className='min-w-[86px]'
                onClick={() => form.handleSubmit()}
              >
                {t('save')}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default memo(UpdatePasswordDialog);
